import { addDoc, collection, doc, getDocs, query, serverTimestamp, where } from "@firebase/firestore";
import { dismissLoadingToast, showErrorToast, showLoadingToast, showSuccessToast, updateLoadingToast } from "app/components/ShowToast";
import axios from "axios";
import db from "services/firebase";

interface AikidoScanResult {
  id: string;
  type: string;
  status: string;
  vulnerabilities: {
    id: string;
    name: string;
    severity: "critical" | "high" | "medium" | "low";
    description: string;
    remediation: string;
    affected_component: string;
    detected_at: string;
  }[];
  risk_assessment: {
    critical: number;
    high: number;
    medium: number;
    low: number;
  };
}

interface AikidoIntegrationResponse {
  success: boolean;
  scanResults?: AikidoScanResult[];
  newCount?: number;
  resolvedCount?: number;
  totalCount?: number;
  error?: string;
}

interface AikidoError {
  response?: {
    status?: number;
    data?: {
      message?: string;
    };
  };
  message?: string;
}

export const createAikidoIntegration = (accessToken: string) => {
  if (!accessToken || typeof accessToken !== "string" || accessToken.trim() === "") {
    throw new Error("A valid access token is required for Aikido integration");
  }

  const axiosInstance = axios.create({
    baseURL: "https://app.aikido.dev/api/v1",
    headers: {
      Authorization: `Bearer ${accessToken}`,
      Accept: "application/json",
    },
  });

  const getUserFriendlyErrorMessage = (error: unknown): string => {
    const aikidoError = error as AikidoError;
    if (aikidoError.response?.status === 403) {
      return "Access forbidden. Please check your access token permissions.";
    } else if (aikidoError.response?.status === 401) {
      return "Invalid access token. Please reconnect to Aikido.";
    } else if (aikidoError.response?.status === 404) {
      return "Resource not found. Please check the repository or scan ID.";
    } else if (aikidoError.response?.status === 429) {
      return "Aikido API rate limit exceeded. Please try again later.";
    } else if (aikidoError.message?.includes("ENOTFOUND") || aikidoError.message?.includes("Network Error")) {
      return "Unable to reach Aikido API. Please check your network connection and try again.";
    }
    return (
      aikidoError.response?.data?.message ||
      aikidoError.message ||
      "An unexpected error occurred."
    );
  };

  const getAllIssues = async (): Promise<AikidoIntegrationResponse> => {
    try {
      const response = await axios.post(
        `https://us-central1-slate-134a0.cloudfunctions.net/fetchAikidoIssues`,
        { accessToken },
        {
          headers: {
            "Content-Type": "application/json",
          },
          withCredentials: true,
        }
      );
      return response.data as AikidoIntegrationResponse;
    } catch (error: any) {
      const errorMessage = getUserFriendlyErrorMessage(error);
      return { success: false, error: errorMessage };
    }
  };

  const syncVulnerabilities = async (tenantId: string, integrationId: string): Promise<AikidoIntegrationResponse> => {
    try {
      const response = await axios.post(
        `https://us-central1-slate-134a0.cloudfunctions.net/syncAikidoVulnerabilities`,
        { 
          accessToken,
          tenantId,
          integrationId
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
          withCredentials: true,
        }
      );
      return response.data as AikidoIntegrationResponse;
    } catch (error: any) {
      const errorMessage = getUserFriendlyErrorMessage(error);
      return { success: false, error: errorMessage };
    }
  };


  const runVulnerabilityScan = async (
    repositoryId: string,
    scanTypes: string[] = [
      "CSPM",
      "SCA",
      "SAST",
      "IaC",
      "container",
      "DAST",
      "license",
      "malware",
      "secrets",
    ]
  ): Promise<AikidoIntegrationResponse> => {
    try {
      const scanResponse = await axiosInstance.post(`/scans`, {
        repository_id: repositoryId,
        scan_types: scanTypes,
      });

      const scanResult = scanResponse.data;

      return {
        success: true,
        scanResults: [
          {
            id: scanResult.id,
            type: scanResult.scan_type,
            status: scanResult.status,
            vulnerabilities: scanResult.vulnerabilities || [],
            risk_assessment: scanResult.risk_assessment || {
              critical: 0,
              high: 0,
              medium: 0,
              low: 0,
            },
          },
        ],
      };
    } catch (error: unknown) {
      const errorMessage = getUserFriendlyErrorMessage(error);
      return { success: false, error: errorMessage };
    }
  };

  const checkScanStatus = async (scanId: string): Promise<AikidoIntegrationResponse> => {
    try {
      const statusResponse = await axiosInstance.get(`/scans/${scanId}`);
      const scan = statusResponse.data;

      return {
        success: true,
        scanResults: [
          {
            id: scan.id,
            type: scan.scan_type,
            status: scan.status,
            vulnerabilities: scan.vulnerabilities || [],
            risk_assessment: scan.risk_assessment || {
              critical: 0,
              high: 0,
              medium: 0,
              low: 0,
            },
          },
        ],
      };
    } catch (error: unknown) {
      const errorMessage = getUserFriendlyErrorMessage(error);
      return { success: false, error: errorMessage };
    }
  };

  return {
    getAllIssues,
    syncVulnerabilities,
    runVulnerabilityScan,
    checkScanStatus,
  };
};

export const exchangeCodeForAikidoToken = async (
  code: string,
  currentUser: any,
  navigate: (path: string, options?: { replace: boolean }) => void
) => {
  let toastId;
  try {
    toastId = showLoadingToast({ message: "Authenticating with Aikido..." });
    const tokenResponse = await fetch(
      `https://us-central1-slate-134a0.cloudfunctions.net/aikidoOAuthCallback?code=${encodeURIComponent(code)}`,
      {
        method: "POST",
        mode: "cors",
        headers: {
          Accept: "application/json",
        },
      }
    );

    const responseText = await tokenResponse.text();
    let data;
    try {
      data = JSON.parse(responseText);
    } catch (e) {
      throw new Error("Invalid response format from server");
    }

    if (!data.access_token || typeof data.access_token !== "string" || data.access_token.trim() === "") {
      throw new Error("No valid access token received from server");
    }

    updateLoadingToast(toastId, { message: "Token received, setting up integration...", type: "info", isLoading: true });
    localStorage.setItem("aikido_access_token", data.access_token);

    const aikidoIntegration = createAikidoIntegration(data.access_token);
    updateLoadingToast(toastId, { message: "Setting up integration...", type: "info", isLoading: true });
    const integrationId = await fetchAndSaveAikidoRepositories(data.access_token, currentUser, navigate);
    if (!integrationId) {
      throw new Error("Failed to create integration");
    }

    updateLoadingToast(toastId, { message: "Syncing vulnerabilities from Aikido...", type: "info", isLoading: true });
    const syncResult = await aikidoIntegration.syncVulnerabilities(currentUser?.tenantId || '', integrationId);

    updateLoadingToast(toastId, { message: "Saving integration data...", type: "info", isLoading: true });
    const finalIntegrationId = await fetchAndSaveAikidoRepositories(data.access_token, currentUser, navigate);

    dismissLoadingToast(toastId);
    showSuccessToast({ message: "Successfully authenticated with Aikido and retrieved vulnerabilities" });
    navigate(`/integrations?id=${finalIntegrationId}&service=Aikido Security`, { replace: true });
  } catch (error: any) {
    showErrorToast({ message: "Aikido authentication or scan failed. Check console for details." });
    dismissLoadingToast(toastId);
    localStorage.removeItem("aikido_access_token");
    navigate("/integrations", { replace: true });
    throw error;
  }
};

export const fetchAndSaveAikidoRepositories = async (
  accessToken: string,
  currentUser: any,
  navigate: (path: string, options?: { replace: boolean }) => void
): Promise<string | undefined> => {
  try {
    const currentDate = new Date();
    const formattedDate = `${currentDate.getFullYear()}-${String(
      currentDate.getMonth() + 1
    ).padStart(2, "0")}-${String(currentDate.getDate()).padStart(2, "0")}`;
    let integrationId: string | undefined;

    const tenantRef = doc(db, "tenants", currentUser?.tenantId);
    const aikidoRef = collection(tenantRef, "integrations");
    const vendorsRef = collection(tenantRef, "vendors");
    const integrationSnapshot = await getDocs(
      query(aikidoRef, where("name", "==", "Aikido Security"))
    );

    if (integrationSnapshot.empty) {
      const newData = {
        accessToken: accessToken,
        name: "Aikido Security",
        group: "Vulnerability Management",
        img_path: "/integrations/aikido.png",
        status: "Connected",
        connectedAt: formattedDate,
        tenantId: currentUser?.tenantId,
        uid: currentUser?.uid,
        createdAt: serverTimestamp(),
      };

      const docRef = await addDoc(aikidoRef, newData);
      integrationId = docRef.id;

      const vendorData = {
        authentication_type: "OAuth",
        category: "Vulnerability Management",
        created_at: serverTimestamp(),
        due_diligence_status: "Incomplete",
        impact_assessment: [],
        logo: [
          {
            name: "aikido_logo.png",
            size: 5000,
            url: "/integrations/aikido.png",
          },
        ],
        owner: currentUser?.userName || "",
        policy_url: "https://www.aikido.dev/privacy-policy",
        reliance: "",
        risk_level: "",
        tenantId: currentUser?.tenantId,
        terms_url: "https://www.aikido.dev/terms-of-service",
        two_factor: true,
        uid: currentUser?.uid,
        updated_at: serverTimestamp(),
        vendor_name: "Aikido Security",
        website_url: "https://www.aikido.dev",
      };

      await addDoc(vendorsRef, vendorData);
    } else {
      integrationId = integrationSnapshot.docs[0].id;
    }

    return integrationId;
  } catch (error) {
    showErrorToast({ message: "Error setting up Aikido integration" });
    navigate("/integrations", { replace: true });
    throw error;
  }
};